import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail,
  FileText,
  Cog,
  ClipboardCheck,
  ListChecks,
  ChevronLeft,
  Check,
  ExternalLink,
} from "lucide-react";
import { crisis, recommendations, approval } from "@/data/fixtures";
import { records } from "@/data/records";
import ApprovalModal from "@/components/ApprovalModal";
import RecordDrawer from "@/components/RecordDrawer";

const NodeShell = ({ index, icon: Icon, label, children, testId, recordKey, onOpenRecord }) => (
  <div
    className="relative mos-chain-line pl-14"
    data-testid={testId}
  >
    <div
      className="absolute left-0 top-1 h-10 w-10 rounded-full flex items-center justify-center border"
      style={{
        background: "var(--mos-surface)",
        borderColor: "var(--mos-border-strong)",
      }}
    >
      <Icon size={16} className="text-slate-700" />
    </div>
    <div className="absolute left-11 top-3 w-4 h-px bg-slate-200" />
    <div className="pb-8">
      <div className="flex items-baseline gap-3 mb-3">
        <span
          className="mos-mono text-[11px] text-slate-500"
          style={{ letterSpacing: "0.08em" }}
        >
          NODE 0{index}
        </span>
        <span className="text-[13px] font-semibold text-slate-900 uppercase tracking-wide">
          {label}
        </span>
        {recordKey && (
          <button
            type="button"
            onClick={() => onOpenRecord(recordKey)}
            className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-[color:var(--mos-accent)] hover:underline font-medium"
            data-testid={`btn-open-record-${recordKey}`}
          >
            <ExternalLink size={11} />
            Open record
          </button>
        )}
      </div>
      {children}
    </div>
  </div>
);

export default function Explainability() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [openRecordKey, setOpenRecordKey] = useState(null);
  const rec = recommendations[0];
  const alt = recommendations[1];

  const onApproved = () => {
    setModalOpen(false);
    toast.success(
      `Signed and logged · ${approval.loggedTimestamp} · re-authenticated`,
      { duration: 4600 }
    );
  };

  return (
    <div data-testid="page-explainability" className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <button
            onClick={() => navigate("/")}
            className="mos-btn mos-btn-ghost -ml-3"
            data-testid="btn-back-briefing"
          >
            <ChevronLeft size={16} />
            Back to briefing
          </button>
          <h1 className="mt-3 text-[32px] leading-tight font-semibold text-slate-900 tracking-tight">
            Reasoning trace ·{" "}
            <span className="mos-mono text-slate-900">{crisis.po}</span>
          </h1>
          <p className="mt-2 text-[14px] text-slate-600 max-w-2xl">
            Five nodes. Each references a system of record. Nothing is a black
            box.
          </p>
        </div>
        <div className="flex flex-col items-end text-[12px] text-slate-500 pt-2">
          <span>Confidence · high</span>
          <span className="mt-1">Trace ID · <span className="mos-mono">rt-2026-05-23-0658</span></span>
        </div>
      </div>

      {/* Chain */}
      <div className="mos-surface rounded-md p-10">
        {/* Node 1 · Source email */}
        <NodeShell
          index={1}
          icon={Mail}
          label="Source · Supplier correspondence"
          testId="node-1-email"
          recordKey="email"
          onOpenRecord={setOpenRecordKey}
        >
          <div
            className="rounded-md border overflow-hidden"
            style={{
              borderColor: "var(--mos-border)",
              background: "var(--mos-surface-2)",
            }}
          >
            <div
              className="px-4 py-3 border-b flex items-center justify-between"
              style={{ borderColor: "var(--mos-border)" }}
            >
              <div>
                <div className="text-[13px] font-semibold text-slate-900">
                  {crisis.emailSubject}
                </div>
                <div className="text-[12px] text-slate-500 mt-0.5">
                  From: production@apex-precision.example · Received{" "}
                  <span className="mos-mono">May 14, 2026 · 08:41</span>
                </div>
              </div>
              <span
                className="text-[11px] uppercase tracking-wider mos-mono"
                style={{ color: "var(--mos-text-subtle)" }}
              >
                wk20
              </span>
            </div>
            <div className="px-4 py-4 text-[13.5px] text-slate-700 leading-relaxed space-y-2">
              <p className="text-slate-500">
                Team — quick roundup on this week&rsquo;s lots. Deburr throughput held
                steady, and the CMM queue is on plan. Two supplier POs at
                Meridian are on-schedule for the promised windows.
              </p>
              <p
                className="rounded px-3 py-2 border-l-2"
                style={{
                  background: "var(--mos-amber-bg)",
                  borderColor: "var(--mos-amber)",
                  color: "#78350F",
                }}
                data-testid="email-highlight"
              >
                {crisis.emailHighlight}
              </p>
              <p className="text-slate-500">
                We will send updated tracking once outbound is booked. Let us
                know if you need a status call before Friday.
              </p>
            </div>
          </div>
          <div className="mt-3 text-[12px] text-slate-500">
            Detected in <span className="mos-mono">{crisis.detectionSource}</span> · nine days ago.
          </div>
        </NodeShell>

        {/* Node 2 · PO record */}
        <NodeShell
          index={2}
          icon={FileText}
          label="Cross-reference · Purchase order"
          testId="node-2-po"
          recordKey="po"
          onOpenRecord={setOpenRecordKey}
        >
          <div className="grid grid-cols-4 gap-x-8 gap-y-4 rounded-md border p-5"
            style={{ borderColor: "var(--mos-border)" }}
          >
            <MetricCell label="Record" value={crisis.po} mono />
            <MetricCell label="Supplier" value={crisis.supplier} />
            <MetricCell label="Original promise" value={crisis.originalDate} mono />
            <MetricCell
              label="Revised promise"
              value={crisis.revisedDate}
              mono
              tone="amber"
            />
            <MetricCell label="Line item" value="titanium locking plate" span={2} />
            <MetricCell label="Lot" value="4471-B" mono />
            <MetricCell label="System" value="SAP" />
          </div>
        </NodeShell>

        {/* Node 3 · Work order */}
        <NodeShell
          index={3}
          icon={Cog}
          label="Dependency · Work order"
          testId="node-3-wo"
          recordKey="wo"
          onOpenRecord={setOpenRecordKey}
        >
          <div
            className="grid grid-cols-4 gap-x-8 gap-y-4 rounded-md border p-5"
            style={{ borderColor: "var(--mos-border)" }}
          >
            <MetricCell label="Work order" value={crisis.workOrder.id} mono />
            <MetricCell label="Site" value={crisis.workOrder.site} />
            <MetricCell
              label="Scheduled start"
              value={crisis.workOrder.scheduledStart}
              mono
            />
            <MetricCell label="Blocked by" value={crisis.po} mono tone="amber" />
          </div>
        </NodeShell>

        {/* Node 4 · Sales order */}
        <NodeShell
          index={4}
          icon={ClipboardCheck}
          label="Downstream · Sales order"
          testId="node-4-so"
          recordKey="so"
          onOpenRecord={setOpenRecordKey}
        >
          <div
            className="rounded-md border p-5"
            style={{ borderColor: "var(--mos-border)" }}
          >
            <div className="grid grid-cols-4 gap-x-8 gap-y-4">
              <MetricCell label="Sales order" value={crisis.salesOrder.id} mono />
              <MetricCell label="Customer" value={crisis.salesOrder.customer} />
              <MetricCell
                label="Committed delivery"
                value={crisis.salesOrder.committedDelivery}
                mono
              />
              <MetricCell
                label="Order value"
                value={crisis.salesOrder.orderValue}
                mono
              />
            </div>
            <div
              className="mt-5 pt-4 border-t flex items-center gap-3 text-[13px]"
              style={{ borderColor: "var(--mos-border)" }}
            >
              <span
                className="mos-mono text-[11px] px-2 py-0.5 rounded"
                style={{
                  background: "var(--mos-amber-bg)",
                  color: "var(--mos-amber)",
                }}
              >
                CLAUSE {crisis.salesOrder.penaltyClause}
              </span>
              <span className="text-slate-700">
                Late-delivery penalty · {crisis.salesOrder.penaltyTerms}.
              </span>
            </div>
          </div>
        </NodeShell>

        {/* Node 5 · Recommendations */}
        <NodeShell
          index={5}
          icon={ListChecks}
          label="Recommendation · Ranked options"
          testId="node-5-recommendations"
        >
          <div className="space-y-4">
            {/* Recommended */}
            <div
              className="rounded-md border p-5"
              style={{
                borderColor: "var(--mos-accent)",
                background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 40%)",
              }}
              data-testid="recommendation-primary"
            >
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[11px] tracking-widest font-semibold px-2 py-0.5 rounded"
                      style={{
                        background: "var(--mos-accent)",
                        color: "#fff",
                      }}
                    >
                      {rec.rank}
                    </span>
                    <span className="text-[15px] font-semibold text-slate-900">
                      {rec.title}
                    </span>
                  </div>
                  <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                    {rec.body}
                  </p>
                  <div className="mt-4 grid grid-cols-4 gap-x-6 gap-y-3">
                    {rec.metrics.map((m) => (
                      <MetricCell
                        key={m.label}
                        label={m.label}
                        value={m.value}
                        mono
                      />
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="mos-btn mos-btn-primary"
                  data-testid="btn-accept-recommendation"
                >
                  <Check size={15} />
                  Accept recommendation
                </button>
              </div>
            </div>

            {/* Alternate */}
            <div
              className="rounded-md border p-5"
              style={{ borderColor: "var(--mos-border)" }}
              data-testid="recommendation-alternate"
            >
              <div className="flex items-center gap-2">
                <span
                  className="text-[11px] tracking-widest font-semibold px-2 py-0.5 rounded border"
                  style={{
                    color: "var(--mos-text-muted)",
                    borderColor: "var(--mos-border-strong)",
                  }}
                >
                  {alt.rank}
                </span>
                <span className="text-[15px] font-semibold text-slate-900">
                  {alt.title}
                </span>
              </div>
              <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                {alt.body}
              </p>
              <div className="mt-4 grid grid-cols-4 gap-x-6 gap-y-3">
                {alt.metrics.map((m) => (
                  <MetricCell
                    key={m.label}
                    label={m.label}
                    value={m.value}
                    mono
                    tone={m.label === "Penalty exposure" ? "amber" : undefined}
                  />
                ))}
              </div>
            </div>
          </div>
        </NodeShell>
      </div>

      <ApprovalModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onApprove={onApproved}
        requiredAuthority="production-commitment"
      />

      <RecordDrawer
        open={!!openRecordKey}
        record={openRecordKey ? records[openRecordKey] : null}
        onClose={() => setOpenRecordKey(null)}
      />
    </div>
  );
}

function MetricCell({ label, value, mono, tone, span }) {
  const color =
    tone === "amber" ? "var(--mos-amber)" : "var(--mos-text)";
  return (
    <div
      className={span === 2 ? "col-span-2" : ""}
      data-testid={`metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </div>
      <div
        className={
          "text-[14px] " +
          (mono ? "mos-mono font-medium " : "font-medium ")
        }
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}
